import type { StrictOmit } from '@frontend/duck-tape';
import type { ClassName } from '@frontend/web-utils';
import { mergeAndConcatClasses, safeCss } from '@frontend/web-utils';
import type { ClickableProps } from '../Containers';
import { XStack } from '../Containers';
import type { IconProps } from '../Icon/Icon';
import { Icon } from '../Icon/Icon';
import type { IconName } from '../Icon/helpers';
import type { TextProps } from '../Texts/Text';
import { Text } from '../Texts/Text';

export type TagProps = {
  activeContainerClassName?: string;
  activeIconColor?: IconProps['color'];
  activeTextColor?: TextProps['color'];
  className?: ClassName;
  containerRadius?: ClassName;
  isActive?: boolean;
  label: string;
  leftIconName?: IconName;
  leftIconSize?: IconProps['size'];
  onClick?: ClickableProps['onClick'];
  onClickLeftIcon?: IconProps['onClick'];
  onClickRightIcon?: IconProps['onClick'];
  rightIconName?: IconName;
  rightIconSize?: IconProps['size'];
  textProps?: StrictOmit<TextProps, 'children'>;
};

/**
 * Currently this Tag component assumes you'll only ever use the right icon for delete purposes (but I named it onClickRightIcon)
 * to imply future intensions of using generalizing the icon.
 */
export const Tag = ({
  activeContainerClassName,
  activeIconColor,
  activeTextColor,
  className,
  containerRadius = safeCss('rounded-xxl'),
  isActive = false,
  label,
  leftIconName,
  leftIconSize,
  onClick,
  onClickLeftIcon,
  onClickRightIcon,
  rightIconName,
  rightIconSize,
  textProps,
}: TagProps) => {
  const baseClassName = mergeAndConcatClasses(
    'border-1 flex items-center justify-center py-xs px-md gap-x-xs max-w-[300px]',
    safeCss(isActive ? 'border-borderSecondary' : 'border-borderTertiary'),
    safeCss(onClick ? 'cursor-pointer hover-opacity-effect' : ''),
    safeCss(isActive ? activeContainerClassName ?? 'bg-surfaceAccent' : 'bg-white'),
    containerRadius,
    className,
  );
  return (
    <XStack className={baseClassName} onClick={onClick}>
      {leftIconName ? (
        <Icon
          color={isActive ? activeIconColor ?? 'primary' : 'tertiary'}
          name={leftIconName}
          onClick={onClickLeftIcon}
          size={leftIconSize || 'xs'}
        />
      ) : null}
      <Text
        color={isActive ? activeTextColor ?? 'primary' : 'tertiary'}
        isSelectable={false}
        lineClamp={1}
        span
        type="captionBold"
        {...textProps}
      >
        {label}
      </Text>
      {rightIconName ? (
        <Icon
          className="p-none"
          color={isActive ? activeIconColor ?? 'primary' : 'tertiary'}
          name={rightIconName}
          onClick={onClickRightIcon}
          size={rightIconSize || 'xs'}
        />
      ) : null}
    </XStack>
  );
};
